import { z } from 'zod';
import { REGEX } from '@/lib/constants';

export const loginSchema = z.object({
    emailOrUsername: z
        .string()
        .min(1, 'Please enter your email or username')
        .refine(
            value => {
                if (value.includes('@')) {
                    return REGEX.email.test(value);
                }
                return true;
            },
            { message: 'Please enter a valid email address' }
        ),
    password: z.string().min(1, 'Please enter a password').min(6, 'Password must be at least 6 characters long'),
});

export const resetPasswordSchema = z
    .object({
        password: z
            .string()
            .min(1, 'Please enter a password')
            .min(6, 'Password must be at least 6 characters long')
            .max(48, 'Please ensure your password is 6 - 48 characters long'),
        confirmPassword: z.string().min(1, 'Please re-enter password'),
    })
    .refine(data => data.password === data.confirmPassword, {
        message: 'Passwords do not match',
        path: ['confirmPassword'],
    });

export const forgotPasswordSchema = z.object({
    email: z
        .string()
        .min(1, 'Please enter your email')
        .refine(value => REGEX.email.test(value), { message: 'Please enter a valid email address' }),
});

export const registerSchema = z.object({
    username: z
        .string()
        .trim()
        .min(1, 'Please enter a username')
        .min(5, 'Username must be at least 5 characters')
        .max(48, 'Username must be less than 48 characters')
        .regex(/^[A-Za-z0-9-_]+$/, 'Username can only contain letters, numbers, dashes & underscores'),
    fullName: z.string().trim().min(1, 'Please enter your name').max(100, 'Name must be less than 100 characters'),
    email: z
        .string()
        .trim()
        .min(1, 'Please enter your email')
        .refine(value => REGEX.email.test(value), { message: 'Please enter a valid email address' }),
    phoneNumber: z.string().min(1, 'Please enter a valid phone number'),
    countryCode: z.string().optional(),
    password: z
        .string()
        .trim()
        .min(1, 'Please enter a password')
        .min(6, 'Password must be at least 6 characters')
        .max(48, 'Password must be less than 48 characters'),
});

export type RegisterFormValues = z.infer<typeof registerSchema>;

// Affiliate registration schema - same as register but without username (comes from token)
export const affiliateRegistrationSchema = z.object({
    fullName: z.string().trim().min(1, 'Please enter your name'),
    email: z.string().trim().min(1, 'Please enter your email').email('Please enter a valid email address'),
    phoneNumber: z.string().min(1, 'Please enter a valid phone number'),
    countryCode: z.string().optional(),
    password: z
        .string()
        .trim()
        .min(1, 'Please enter a password')
        .min(6, 'Password must be at least 6 characters')
        .max(48, 'Password must be less than 48 characters'),
});

export type AffiliateRegistrationFormValues = z.infer<typeof affiliateRegistrationSchema>;

// Affiliate login schema - reuses loginSchema pattern
export const affiliateLoginSchema = z.object({
    emailOrUsername: z.string().min(1, 'Please enter your email or username'),
    password: z.string().min(1, 'Please enter your password'),
});

export type AffiliateLoginFormValues = z.infer<typeof affiliateLoginSchema>;
